// Career Map — шапка и подвал страниц карты профессий (и /coaches).
//
// С 06.10.2026 это не своя разметка, а общая шапка сайта
// (src/components/site/SiteChrome.tsx): раньше здесь жила копия старой
// статической шапки с «Как это работает» и «Пример отчёта», уводившими на
// главную. Здесь остаётся только то, что знает карта: куда ведёт
// переключатель языка (та же сфера или профессия на другом языке) и
// атрибуция O*NET в подвале.

import type { Lang } from "./spheres";
import { SiteFooter, SiteHeader, type SiteSection } from "@/components/site/SiteChrome";

const CAREERS: Record<Lang, string> = { az: "/az/careers", ru: "/careers", en: "/en/careers" };
const COACHES: Record<Lang, string> = { az: "/az/coaches", ru: "/coaches", en: "/en/coaches" };
const LANGS: Lang[] = ["az", "ru", "en"];

/** Переключатель языка оставляет на той же странице: slug сферы и профессии
 *  от языка не зависит, поэтому профессия открывается та же. Одна функция —
 *  для шапки и подвала. */
export function careersLangHref(
  active: Extract<SiteSection, "careers" | "coaches">,
  sphereSlug?: string,
): Record<Lang, string> {
  return Object.fromEntries(
    LANGS.map((l) => [l, active === "coaches" ? COACHES[l] : sphereSlug ? `${CAREERS[l]}/${sphereSlug}` : CAREERS[l]]),
  ) as Record<Lang, string>;
}

export function CareersNav({
  lang,
  sphereSlug,
  active = "careers",
}: {
  lang: Lang;
  sphereSlug?: string;
  active?: Extract<SiteSection, "careers" | "coaches">;
}) {
  return <SiteHeader lang={lang} active={active} langHref={careersLangHref(active, sphereSlug)} />;
}

export function CareersFooter({
  lang,
  sphereSlug,
  active = "careers",
}: {
  lang: Lang;
  sphereSlug?: string;
  active?: Extract<SiteSection, "careers" | "coaches">;
}) {
  return (
    <SiteFooter
      lang={lang}
      active={active}
      langHref={careersLangHref(active, sphereSlug)}
      note={active === "careers" ? "O*NET · CC BY 4.0" : undefined}
    />
  );
}

// ── Sphere icons ─────────────────────────────────────────────────────────
// Line icons, navy via currentColor, on the #E0EDF1 plaque.
const ICON_PATHS: Record<string, React.ReactNode> = {
  code: (
    <>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </>
  ),
  finance: (
    <>
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </>
  ),
  marketing: (
    <>
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </>
  ),
  engineering: (
    <path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L3 18v3h3l6.1-6.1a4 4 0 0 0 5.6-5.6l-2.9 2.9-2-2 2.9-2.9z" />
  ),
  health: (
    <>
      <rect x="9" y="4" width="6" height="16" rx="2" />
      <rect x="4" y="9" width="16" height="6" rx="2" />
    </>
  ),
  education: (
    <>
      <path d="M22 10 12 5 2 10l10 5 10-5z" />
      <path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5" />
    </>
  ),
  creative: <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />,
  law: (
    <>
      <path d="M12 3v18" />
      <path d="M6 7h12" />
      <path d="M6 7 3 14a3 3 0 0 0 6 0z" />
      <path d="M18 7l-3 7a3 3 0 0 0 6 0z" />
      <path d="M8 21h8" />
    </>
  ),
  services: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polygon points="16 8 10.5 10.5 8 16 13.5 13.5" />
    </>
  ),
  agro: (
    <>
      <path d="M7 20h10" />
      <path d="M12 20V9" />
      <path d="M12 9a5 5 0 0 0-5-5H5v2a5 5 0 0 0 5 5h2z" />
      <path d="M12 9a5 5 0 0 1 5-5h2v2a5 5 0 0 1-5 5h-2z" />
    </>
  ),
};

export function SphereIcon({ icon }: { icon: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[icon] ?? ICON_PATHS.code}
    </svg>
  );
}
